// Internal modules
import { appStore } from '@/store';
import { logger } from '../logger';
import { features } from '../features';
import { isDev } from '../utils/env';
import { apolloClient } from '../apollo';
import { getGlobals, getLocale } from '../globals';
import { redirectToGenericError } from '../utils/redirects';
import {
  buildCrsCountryList,
  mapGriffinMetadata,
  // @ts-expect-error - untyped import from JS file
} from '../../components/App/utils';
import { fetchJson, getAddressMetaKey } from '../utils/miscUtils';
// @ts-expect-error untyped import
import { formatGriffinAddressMetadata } from '../utils/responseMappers';
import { ADDRESS_METADATA_QUERY, GRIFFIN_METADATA_QUERY } from '../graphql';
import {
  getContent,
  hasCachedContentForLocale,
  resetContent,
  updateContentCache,
} from './content';
import type { GenericObjectType } from '@/types';

/**
 * @function fetchContentManifest
 * @returns {promise}
 * @description Fetches the content manifest from the appropriate source
 * based on the environment. The manifest is needed for when the language
 * changes so that we can get the hash of the content file for a particular
 * locale.
 */
const fetchContentManifest = (): Promise<void | GenericObjectType> => {
  // In dev mode we don't need a manifest since we fetch the content
  // from the file system.
  if (isDev) {
    return Promise.resolve();
  }

  const manifestUrl = getGlobals().uri.contentManifestUrl;
  logger.info('fetching_content_manifest', {
    url: manifestUrl,
  });

  return fetchJson(manifestUrl)
    .then(manifest => (CONTENT_MANIFEST.manifest = manifest))
    .catch(err => {
      /**
       * We don't throw the error in this block because most users don't change
       * their language or country. If a user does end up changing their
       * language or country we'll see that `CONTENT_MANIFEST.hasError` is
       * `true` and we'll throw an error and redirect to the generic error page
       * at that time.
       */
      logger.error('error_fetching_content_manifest');
      CONTENT_MANIFEST.hasError = true;
    });
};

// Keeping a reference to the promise so that we can hang .then()s off it so
// that we don't try to fetch a content file before we know what the hash for
// that file is. Content file names are in the form of `{lang}.{hash}.json`.
interface ContentManifest {
  manifest: null | GenericObjectType;
  promise: ReturnType<typeof fetchContentManifest>;
  hasError?: boolean;
}
const CONTENT_MANIFEST: ContentManifest = {
  manifest: null,
  promise: fetchContentManifest(),
};

// HELPER FUNCTIONS
const checkForApiError = (data: GenericObjectType) => {
  // TODO: Can we get a more specific TS type from griffin API?
  const error = data?.errors?.[0];
  if (error) {
    throw error;
  }
};

interface CountryAndLanguage {
  country: string;
  language: string;
}
const getContentFileName = ({ country, language }: CountryAndLanguage) => {
  const key = `${country}_${language}`;
  const hash = !isDev ? CONTENT_MANIFEST.manifest?.[key] : '';

  // If we are in dev mode then `hash` === '' so the file name
  // will have a `..` in it. e.g. `en..json`
  return {
    fileName: `${language}.${hash}.json`.replace('..', '.'),
    hash,
  };
};

const getContentUrl = ({ country, language }: CountryAndLanguage) => {
  const { fileName, hash } = getContentFileName({ country, language });
  return {
    hash,
    url: `${getGlobals().uri.contentEndpoint}${country}/${fileName}`,
  };
};

const handleApiError = (operation: string, error: Error) => {
  logger.error(`${operation}_error`, {
    message: error.message,
  });

  redirectToGenericError();
};

/**
 * Merges locale overrides with the current locale
 */
const mergeLocale = (overrides?: Partial<CountryAndLanguage>) => {
  const locality = getLocale();
  const country = overrides?.country || locality.country;
  const language = overrides?.language || locality.language;
  const locale = `${language}_${country}`;

  return {
    country,
    language,
    locale,
  };
};

/**
 * Fetches content from the CDN. The call the fetch the content
 * waits for the content manifest retrieval before executing.
 */
export const fetchContent = (
  localeOverrides?: Partial<CountryAndLanguage>,
): Promise<void> => {
  // If the user changes content before the content manifest has been
  // fetched then we need to wait for it before we can proceed because
  // we don't know the hash for the content file we need to fetch.
  const contentManifestPromise = CONTENT_MANIFEST.promise;
  return contentManifestPromise.then(() => {
    if (CONTENT_MANIFEST.hasError) {
      logger.error('no_content_manifest_available');
      return redirectToGenericError();
    }

    const { country, language } = mergeLocale(localeOverrides);
    const { hash, url: contentUrl } = getContentUrl({ country, language });
    const logPayload = {
      contentUrl,
      hash,
      locale: `${language}_${country}`,
    };
    return fetchJson(contentUrl)
      .then(content => {
        logger.info('content_successfully_fetched', logPayload);
        updateContentCache({ content, hash });
      })
      .catch(error => {
        logger.error('content_fetched_failed', {
          ...logPayload,
          message: error.message,
        });
        redirectToGenericError();
      });
  });
};

/**
 * Fetches content from the CDN and then resets the
 * `getContent()` function to use the new content.
 */
export const fetchAndResetContent = (
  localeOverrides?: Partial<CountryAndLanguage>,
): Promise<void> => {
  return fetchContent(localeOverrides).then(resetContent);
};

/**
 * @function fetchContentAndMetadata
 * @description Handles fetching content for the CDN and fetching Griffin
 * metadata when the language is changed by the user. Will only make API
 * calls if the metadata data isn't available in the store and the content
 * isn't available in the content cache.
 */
export const fetchContentAndMetadata = (): void => {
  const { api: apiActions, ui: uiActions } = appStore.getActions();
  uiActions.toggleIsPageLoading(true);

  const { country, language, locale } = getLocale();
  const { api: apiState, form: formState } = appStore.getState();
  const { griffinMetadata } = apiState;

  // Helper function to build CRS list when both content and Griffin metadata are ready
  const buildCrsListIfReady = () => {
    if (!features.isActive('shouldShowCRSTaxCollectionSection')) {
      return;
    }

    const currentApiState = appStore.getState().api;

    // Check if content is available for the current locale
    if (!hasCachedContentForLocale(locale)) {
      // Content not ready yet, can't build CRS list
      return;
    }

    // Check if Griffin metadata exists for this locale
    if (currentApiState.griffinMetadata?.territories?.[locale]?.length > 0) {
      const existingCrsData = currentApiState.crsTaxResidencyCountries;

      // Only build if not already built for this locale
      if (!existingCrsData?.territories?.[locale]?.length) {
        // TODO: [DETXOCOMPL-32] Remove buildCrsCountryList call once
        // GriffinMetadataQuery is extended. Replace with direct usage of assigned
        // country list from Griffin metadata response
        const territories = buildCrsCountryList(
          currentApiState.griffinMetadata,
          getContent,
          locale,
        );

        apiActions.setCrsTaxResidencyCountries({
          territories: {
            [locale]: territories,
          },
        });
      }
    }
  };

  // Fetch or load content
  if (!hasCachedContentForLocale(locale)) {
    logger.info('fetch_content_from_cdn', { locale });
    fetchAndResetContent().then(() => {
      buildCrsListIfReady();
      uiActions.toggleIsPageLoading(false);
    });
  } else {
    logger.info('set_content_from_cache', { locale });
    resetContent();
    buildCrsListIfReady();
    uiActions.toggleIsPageLoading(false);
  }

  // If we don't have the griffin metadata with labels for address fields and
  // territory names in the newly selected language then we have to fetch it
  // from the API.
  if (
    !griffinMetadata.territories[locale] ||
    !griffinMetadata.territoriesByRegion[locale] ||
    !griffinMetadata.address[getAddressMetaKey()]
  ) {
    logger.info('fetch_griffin_metadata', { locale });
    apolloClient
      .query({
        query: GRIFFIN_METADATA_QUERY,
        variables: {
          countryCode: country,
          languageCode: language,
          shippingCountryCode: country,
        },
      })
      .then(({ data }) => {
        checkForApiError(data);

        const { localeMetadata = {} } = data;
        const griffinMetadata = mapGriffinMetadata(localeMetadata, country);
        apiActions.setGriffinMetadata(griffinMetadata);

        buildCrsListIfReady();
      })
      .catch(error => {
        handleApiError('language_change_griffin_metadata', error);
      });
  }

  // If the shipping country is not the same as the billing country and we
  // don't have the metadata for the shipping country in the new language,
  // then we need to fetch it get the labels in the newly selected language.
  const { value: shippingCountry } = formState.shippingAddress.country;
  if (
    shippingCountry !== country &&
    !griffinMetadata.address[getAddressMetaKey(shippingCountry)]
  ) {
    logger.info('fetch_address_metadata', {
      country: shippingCountry,
      language,
    });
    apolloClient
      .query({
        query: ADDRESS_METADATA_QUERY,
        variables: {
          countryCode: shippingCountry,
          languageCode: language,
        },
      })
      .then(({ data }) => {
        checkForApiError(data);

        const { localeMetadata: metadata } = data;
        const addressMeta = formatGriffinAddressMetadata(metadata.address);
        apiActions.addAddressMetadata({
          key: getAddressMetaKey(shippingCountry),
          payload: addressMeta,
        });
      })
      .catch(error => {
        handleApiError('address_metadata', error);
      });
  }
};
